import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  PageSection,
  PageSectionVariants,
  ToolbarItem,
  Pagination,
  ToolbarContent,
  Content,
  Divider,
  Select,
  SelectList,
  SelectOption,
  MenuToggle
} from '@patternfly/react-core';
import { Level, LevelItem } from '@patternfly/react-core';
import { AuthContext } from 'shared/context/AuthContext';
import { ExercisesTable } from '../features/exercises/components/ExercisesTable';
import { Icons } from "shared/components/Icons";
import { SpinnerPage } from 'shared/components/spinner/page/spinnerPage'


const Exercises = (props) => {
  const [exercisesData, setExercisesData] = useState(null);
  const [loadingExercises, setLoadingExercises] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('Grupy');

  const context = useContext(AuthContext);
  const location = useLocation();
  const params = useParams();

  // Ustalanie typu ćwiczeń
  const routeExerciseType = params.exercise_type;
  const exerciseTypeFromProps = props?.state?.exercise_type;
  const defaultExerciseType = 'ls';
  const resolvedExerciseType = routeExerciseType || exerciseTypeFromProps || defaultExerciseType;

  const apiReady = props?.state?.api_url && props?.state?.organization_symbol;
  
  // fetchExercises zawsze wywołany, hook w tej samej kolejności
  const fetchExercises = useCallback(async (exercise_type) => {
    if (!exercise_type || !apiReady) return;

    try {
      setLoadingExercises(true);
      const org = props.state.organization_symbol;
      const pathParts = location.pathname.split("/").filter(Boolean);
      const branch =
        props.state.branch_symbol ||
        pathParts[1] || // zależy od struktury URL
        "ti";
      const url = `${props.state.api_url}/ti/api/v1/${org}/${branch}/exercises/${exercise_type}/?page=${page}&limit=${perPage}`;
      
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
      const data = await resp.json();

      const results = data.results || data;

      setExercisesData({
        count: data.count || (results.exercises ? results.exercises.length : results.length || 0),
        headers: results.headers || [],
        exercises: results.exercises || results,
        execution_time: results.execution_time || 0
      });
    } catch (err) {
      console.error('Exercises fetch error:', err);
      setExercisesData(null);
    } finally {
      setLoadingExercises(false);
    }
  }, [props.state.api_url, props.state.organization_symbol, props.state.branch_symbol, page, perPage, apiReady]);

  useEffect(() => {
    if (apiReady) fetchExercises(resolvedExerciseType);
  }, [location.pathname, page, perPage, resolvedExerciseType, fetchExercises, apiReady]);

  const renderPagination = (variant = 'top') => {
    if (!loadingExercises && exercisesData) {
      const itemCount = exercisesData.count || 0;
      return (
        <Pagination
          isCompact
          itemCount={itemCount}
          page={page}
          perPage={perPage}
          onSetPage={(_evt, value) => setPage(value)}
          onPerPageSelect={(_evt, value) => {
            setPage(1);
            setPerPage(value);
          }}
          variant={variant}
        />
      );
    }
  };

  const updateExercise = (exercise_id, solution_shared) => {
    setExercisesData(prev => {
      if (!prev || !prev.exercises) return prev;
      const updatedExercises = prev.exercises.map(ex =>
        ex.exercise_id === exercise_id ? { ...ex, solution_shared } : ex
      );
      return { ...prev, exercises: updatedExercises };
    });
  };
  
  const onToggleClick = () => setIsOpen(!isOpen);
  const onSelect = (_event, value) => {
    setSelected(value);
    setIsOpen(false);
  };
  const toggle = (toggleRef) => (
    <MenuToggle ref={toggleRef} onClick={onToggleClick} isExpanded={isOpen} style={{ width: '200px' }}>
      {selected}
    </MenuToggle>
  );

  // JSX
  if (!apiReady || loadingExercises) {
    return (
      <SpinnerPage themeVersion="" isLoaded={!loadingExercises} textSpinner={location?.pathname?.split("/")[4]?.toUpperCase()} className="" />
    );
  }

  return (
    <>
      {exercisesData && (
        <>
          <PageSection variant={PageSectionVariants.light}>
            <Level>
              <LevelItem>
                {exercisesData.exercises && exercisesData.exercises.length > 0 && (
                  <Content>
                    {exercisesData.exercises[0].short_description || ""}
                  </Content>
                )}
              </LevelItem>
              <LevelItem>
                {exercisesData.exercises && exercisesData.exercises.length > 0 && (
                  <Content>
                    <span style={{ display: 'inline-block', verticalAlign: 'middle', fontSize: '0.9em', marginRight: '20px'}}>
                      {Icons[exercisesData.exercises[0].exercise_type_name] || null}{" "}
                    </span>
                  </Content>
                )}
              </LevelItem>
              <LevelItem>
                <ToolbarContent>
                  <ToolbarItem variant="pagination">{renderPagination()}</ToolbarItem>
                </ToolbarContent>
              </LevelItem>
            </Level>
            <Divider />
            <ExercisesTable
              props={props}
              state={props.state}
              context={context}
              header={exercisesData.headers}
              exercises={exercisesData.exercises}
              updateExercise={updateExercise}
              SelectGroupMenu={
                <Select
                  key="single-grouped-select"
                  id="single-grouped-select"
                  isOpen={isOpen}
                  selected={selected}
                  onSelect={onSelect}
                  onOpenChange={(open) => setIsOpen(open)}
                  toggle={toggle}
                  shouldFocusToggleOnSelect
                >
                  <SelectList>
                    {(exercisesData.exercises || []).flatMap((e) =>
                      Object.values(e.groups_completed || {}).map((g) => g.group_symbol)
                    )
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((group) => <SelectOption key={group} value={group}>{group}</SelectOption>)}
                  </SelectList>
                </Select>
              }
            />
            {(exercisesData.count || 0) > perPage && (
              <ToolbarContent>
                <ToolbarItem variant="pagination">{renderPagination()}</ToolbarItem>
              </ToolbarContent>
            )}
          </PageSection>
        </>
      )}

      {!exercisesData && !loadingExercises && (
        <PageSection variant={PageSectionVariants.light}>
          <Content>Brak danych do wyświetlenia.</Content>
        </PageSection>
      )}
    </>
  );
};

export default Exercises;
